import SearchBar from '@/atoms/forms/SearchBar';
import Button, { BUTTON_VARIANTS } from '@/atoms/Button';
import { useTranslation } from 'react-i18next';
import ListItem from '@/molecules/ListItem';
import UserLink from '@/atoms/UserLink';
import ModalClassicLayout from 'context/Modal/layout/Classic';
import { FormProvider } from 'react-hook-form';
import Spinner from '@/atoms/loader/Spinner';
import { SIZE } from 'types/utils';
import useManagePodEntrantModal from './useManagePodEntrantModal';

type Props = {
  closeModal: () => void;
};

const ManagePodEntrantModal = ({ closeModal }: Props) => {
  const { handleSubmit, formMethods, data, handleAddUser, participants, handleRemoveUser, isLoading, dataIsLoading } =
    useManagePodEntrantModal();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <ModalClassicLayout closeModal={closeModal} title="printing.manage.participants">
        <div className="flex justify-center items-center py-8">
          <Spinner size={SIZE.LG} />
        </div>
      </ModalClassicLayout>
    );
  }

  return (
    <ModalClassicLayout closeModal={closeModal} title="printing.manage.participants">
      <div className="flex flex-col gap-6">
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold mb-4">{t('printing.add.participant')}</h3>
          <FormProvider {...formMethods}>
            <form className="mb-4" onSubmit={handleSubmit}>
              <SearchBar
                isLoading={dataIsLoading}
                name="uniqueId"
                placeholder={t('printing.search.user.placeholder')}
              />
            </form>
          </FormProvider>
          {data && (
            <ListItem
              footerProps={{
                children: <Button onClick={() => handleAddUser(data?.id || '')}>{t('add')}</Button>,
              }}
              headerProps={{
                imageNode: data?.uniqueId && (
                  <UserLink
                    avatarPath={data?.avatarPath}
                    nickName={data?.nickName}
                    subLine={data?.uniqueId}
                    userUniqId={data?.uniqueId}
                  />
                ),
              }}
            />
          )}
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">{t('printing.participants.list')}</h3>
          {participants && participants.length > 0 ? (
            <div className="flex flex-col gap-2">
              {participants.map(
                (participant) =>
                  !!participant.uniqueId && (
                    <ListItem
                      key={participant.id}
                      footerProps={{
                        children: (
                          <Button
                            onClick={() => handleRemoveUser(participant.id || '')}
                            variant={BUTTON_VARIANTS.TERTIARY}
                          >
                            {t('remove')}
                          </Button>
                        ),
                      }}
                      headerProps={{
                        imageNode: (
                          <UserLink
                            avatarPath={participant.avatarPath}
                            nickName={participant.nickName}
                            subLine={participant.uniqueId}
                            userUniqId={participant.uniqueId}
                          />
                        ),
                      }}
                    />
                  ),
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">{t('printing.no.participants')}</p>
          )}
        </div>
      </div>

      <div className="w-fit mx-auto mt-10">
        <Button onClick={closeModal}>{t('close')}</Button>
      </div>
    </ModalClassicLayout>
  );
};

export default ManagePodEntrantModal;
